import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { getTheme, toggleTheme, type Theme } from "../theme";

/* Tynne linje-ikoner — currentColor, matcher «quiet instrument»-stilen */
type IconProps = { size?: number };
const Icon = ({ size = 22, children }: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconOverview = (p: IconProps) => (
  <Icon {...p}><path d="M4 19V5" /><path d="M4 19h16" /><path d="M8 16l4-6 3 3 4-7" /></Icon>
);
const IconRealtime = (p: IconProps) => (
  <Icon {...p}><circle cx="12" cy="12" r="2.4" /><path d="M6.3 6.3a8 8 0 0 0 0 11.4" /><path d="M17.7 6.3a8 8 0 0 1 0 11.4" /></Icon>
);
const IconIssues = (p: IconProps) => (
  <Icon {...p}><path d="M12 4 2.5 20h19L12 4Z" /><path d="M12 10v4" /><path d="M12 17.5v.01" /></Icon>
);
const IconVitals = (p: IconProps) => (
  <Icon {...p}><path d="M3 12a9 9 0 0 1 18 0" /><path d="M12 12l4-3" /><path d="M5 18.5h14" /></Icon>
);
const IconMore = (p: IconProps) => (
  <Icon {...p}><circle cx="5" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="19" cy="12" r="1.3" /></Icon>
);

const IconFunnels = (p: IconProps) => (
  <Icon {...p}><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></Icon>
);
const IconUptime = (p: IconProps) => (
  <Icon {...p}><path d="M3 12h4l2 6 4-14 2 8h6" /></Icon>
);
const IconReleases = (p: IconProps) => (
  <Icon {...p}><path d="M4 7v10l8 4 8-4V7l-8-4-8 4Z" /><path d="m4 7 8 4 8-4" /><path d="M12 11v10" /></Icon>
);
const IconProjects = (p: IconProps) => (
  <Icon {...p}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" /></Icon>
);

const PRIMARY = [
  { to: "/", label: "Oversikt", Icon: IconOverview, end: true },
  { to: "/realtime", label: "Sanntid", Icon: IconRealtime },
  { to: "/issues", label: "Feil", Icon: IconIssues },
  { to: "/vitals", label: "Ytelse", Icon: IconVitals },
];

const SECONDARY = [
  { to: "/funnels", label: "Funnels", Icon: IconFunnels },
  { to: "/uptime", label: "Oppetid", Icon: IconUptime },
  { to: "/releases", label: "Releases", Icon: IconReleases },
  { to: "/projects", label: "Prosjekter", Icon: IconProjects },
];

export function BottomNav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState<Theme>(getTheme());
  const loc = useLocation();

  // Lukk arket ved navigasjon
  useEffect(() => setOpen(false), [loc.pathname]);

  // Lås body-scroll og lukk på Escape mens arket er åpent
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const moreActive = SECONDARY.some((s) => loc.pathname.startsWith(s.to) && s.to !== "/");

  return (
    <>
      {open && (
        <div className="sheet-scrim" onClick={() => setOpen(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Mer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-grip" />
            <div className="sheet-grid">
              {SECONDARY.map(({ to, label, Icon }) => (
                <NavLink key={to} to={to} className="sheet-item">
                  <Icon size={24} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>

            <button
              className="sheet-line"
              onClick={() => setThemeState(toggleTheme())}
            >
              <span>{theme === "dark" ? "☾  Mørkt tema" : "☀  Lyst tema"}</span>
              <span className="sheet-line-hint">bytt</span>
            </button>

            <div className="sheet-foot">
              <span className="email">{user?.email}</span>
              <button className="copy" onClick={() => logout()}>Logg ut</button>
            </div>
          </div>
        </div>
      )}

      <nav className="bottomnav" aria-label="Hovednavigasjon">
        {PRIMARY.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} className="bn-item">
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          className={`bn-item bn-more${moreActive || open ? " on" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="Mer"
        >
          <IconMore />
          <span>Mer</span>
        </button>
      </nav>
    </>
  );
}
